import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {skipWhile, map, shareReplay, take} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import memo from 'memo-decorator';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import {LpAssetService} from './lp-asset.service';
import {LocationDetailsResponse} from 'src/app/shared/models/LocationDetailsResponse.model';
import HoldingType from 'src/app/shared/enums/HoldingType.enum';
import InvestmentType from 'src/app/shared/enums/InvestmentType.enum';
import {LpUiService} from 'src/app/lp/shared/lp-ui.service';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {StorageService} from 'src/app/services/storage.service';
import {ResourceService} from 'src/app/services/resource.service';
import AreaUnit from 'src/app/shared/enums/AreaUnit.enum';
import {RoutingService} from 'src/app/services/routing.service';
import {TransactionStatus} from 'src/app/lp/models/transactionStatus.enum';
import {StorageObjectType} from 'src/app/shared/enums/StorageFileType.enum';
import {LpHoldingService} from '../../shared/holdings/lp-holding.service';
import HoldingStatus from 'src/app/shared/enums/HoldingStatus.enum';
import InvestmentSecurityType from '../../../shared/enums/InvestmentSecurityType.enum';
import {CountryCodePipe} from '../../../shared/pipes/countryCode.pipe';
import {HoldingDiscriminator} from "../../../shared/enums/HoldingDiscriminator.enum";

@Component({
  selector: 'terra-lp-asset',
  templateUrl: './lp-asset.component.html',
  styleUrls: ['./lp-asset.component.scss'],
  providers: [LpAssetService,
    {provide: LpHoldingService, useExisting: LpAssetService}
  ]
})
export class LpAssetComponent extends OnDestroyMixin implements OnInit {
  EXAMPLE_ASSET_ID = TerraUtils.consts.EXAMPLE_ASSET_ID;

  // Enums
  HoldingType = HoldingType;
  SecurityType = InvestmentSecurityType;
  InvestmentType = InvestmentType;
  AreaUnit = AreaUnit;
  HoldingDiscriminator = HoldingDiscriminator;
  TransactionStatus = TransactionStatus;
  StorageObjectType = StorageObjectType;
  HoldingStatus = HoldingStatus;
  opportunityCode: string;
  isOpportunity: boolean;

  assetId$ = this.lpAssetService.holdingId$;
  asset$ = this.lpAssetService.assetDetails$;

  // TODO: fix corner case were the asset is fundAsset and funded by other investors
  isFunded$ = this.asset$.pipe(map(asset => asset.status === HoldingStatus.Owned || !this.opportunityCode), shareReplay(1));

  isFundAsset$ = this.asset$.pipe(map(asset => !!asset.fundId && asset.isInvestedInFund), shareReplay(1));

  originFundId$ = this.lpAssetService.originFundId$;

  originFundBasicInfo$ = this.lpAssetService.originFundBasicInfo$;

  parentFund$ = this.asset$.pipe(map(asset => !!asset.fund), shareReplay(1));

  isInvestedByOriginFund$ = this.originFundBasicInfo$.pipe(map(originFundBasicInfo => originFundBasicInfo !== null));

  get holdingsPageUrl() {
    return this.routingService.lpHoldingsPage;
  }

  constructor(
    private lpAssetService: LpAssetService,
    private routingService: RoutingService,
    private lpUiService: LpUiService,
    private titleService: Title,
    private resourceService: ResourceService,
    private storageService: StorageService,
    private router: Router,
    private telemetryService: TelemetryService,
    private activatedRoute: ActivatedRoute,
    private countryCodePipe: CountryCodePipe
  ) {
    super();
  }

  ngOnInit() {
    // opportunity code for marketing deck
    this.opportunityCode = this.activatedRoute.snapshot.queryParams.oppCode;
    this.isOpportunity = this.opportunityCode !== undefined;
    // Set tab title
    this.asset$
      .pipe(untilComponentDestroyed(this), skipWhile(asset => !asset))
      .subscribe(asset => {
        this.titleService.setTitle('Asset Details: ' + asset.name + TerraUtils.consts.GpPageTitleSuffix);
      });

    this.addViewTelemetryEvent();
  }

  removeExampleAsset() {
    this.storageService.setPersistent(this.storageService.IS_EXAMPLE_ASSET_REMOVED_LOCAL_STORAGE_KEY, 'true');
    this.router.navigateByUrl(this.routingService.lpHoldingsPage);
  }

  @memo()
  getFundPageUrl(fundId: number) {
    return this.routingService.lpFund(fundId);
  }

  @memo()
  backUrl(originFundId: number = null) {
    if (originFundId) {
      return this.getFundPageUrl(originFundId);
    }

    return this.routingService.lpHoldingsPage;
  }

  getBankAccountEditPage(assetId: number) {
    // return this.routingService.holdingBankAccountPreferenceEditor(assetId);
    return this.routingService.lpBankAccounts;
  }

  getMarketingDeckPage() {
    return this.routingService.lpOfferingDeck(this.opportunityCode);
  }

  onContactGpClick() {
    this.telemetryService.create({
      eventID: '205',
      eventTitle: 'LP CONTACTED GP VIA PORTAL',
      additional: {
        isMobileDevice: TerraUtils.isMobileDevice()
      }
    }, AnalyticsServiceNameModel.Insights);
  }

  private addViewTelemetryEvent() {
    let eventId, eventTitle;
    this.asset$.pipe(take(1)).subscribe(asset => {
      switch (asset.status) {
        case HoldingStatus.Fundraising: {
          if (this.isOpportunity) {
            eventId = '207';
            eventTitle = 'LP ASSET OPPORTUNITY VIEW';
          } else {
            eventId = '206';
            eventTitle = 'LP PENDING ASSET VIEW';
          }
          break;
        }
        case HoldingStatus.Owned: {
          eventId = '8';
          eventTitle = 'LP ASSET VIEW';
          break;
        }
        default:
          return;
      }

      this.lpAssetService.holdingId$.pipe(take(1)).subscribe(holdingId => {
        this.telemetryService.create({
          eventID: eventId,
          eventTitle: eventTitle,
          holdingID: holdingId,
          additional: {
            isMobileDevice: TerraUtils.isMobileDevice()
          }
        }, AnalyticsServiceNameModel.Insights);
      });
    });
  }

  additionalInformationClickEventHandler() {
    this.lpAssetService.holdingId$.pipe(take(1)).subscribe(holdingId => {
      this.telemetryService.create({
        eventID: '216',
        eventTitle: 'LP CLICKED GET ADDITIONAL INFORMATION',
        holdingID: holdingId,
        additional: {
          isMobileDevice: TerraUtils.isMobileDevice()
        }
      }, AnalyticsServiceNameModel.Insights);
    });
  }
}
